import { Controller } from "@hotwired/stimulus"

let navigationAnchor

export default class extends Controller {
  static targets = ["scroller", "chip", "previous", "next"]
  static values = { key: String }

  connect() {
    this.restoreNavigation()
    this.update()
  }

  remember(event) {
    if (!this.followsInCurrentTab(event, event.currentTarget.target)) return

    navigationAnchor = {
      key: this.keyValue,
      href: event.currentTarget.href,
      left: event.currentTarget.getBoundingClientRect().left
    }
  }

  navigate(event) {
    if (!navigationAnchor) return

    if (event.detail.action === "restore" || event.detail.url.toString() !== navigationAnchor.href) {
      navigationAnchor = undefined
    }
  }

  scrolled() {
    this.update()
  }

  move(event) {
    const forward = event.params.direction > 0
    const view = this.view()
    const chips = forward ? this.chipTargets : [ ...this.chipTargets ].reverse()
    const chip = chips.find((item) => {
      const bounds = item.getBoundingClientRect()
      return forward ? bounds.left >= view.right - 1 : bounds.right <= view.left + 1
    })

    const left = chip ? this.revealDistance(chip.getBoundingClientRect(), view) : this.remainingDistance(forward)
    if (Math.abs(left) < 1) return

    this.scrollerTarget.scrollBy({ left, behavior: this.behavior() })
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.scrollerTarget

    this.toggle(this.previousTarget, scrollLeft > 1)
    this.toggle(this.nextTarget, scrollLeft < scrollWidth - clientWidth - 1)
  }

  restoreNavigation() {
    const anchor = navigationAnchor
    const preview = document.documentElement.hasAttribute("data-turbo-preview")

    if (!preview) navigationAnchor = undefined
    if (!anchor) return

    const chip = this.activeChip
    if (anchor.key !== this.keyValue || anchor.href !== chip.href) return

    const left = chip.getBoundingClientRect().left - anchor.left
    if (Math.abs(left) < 1) return

    this.scrollerTarget.scrollBy({ left, behavior: "auto" })
  }

  revealDistance(bounds, view) {
    if (bounds.left < view.left) return bounds.left - view.left
    if (bounds.right > view.right) return bounds.right - view.right

    return 0
  }

  remainingDistance(forward) {
    const { scrollLeft, scrollWidth, clientWidth } = this.scrollerTarget

    return forward ? scrollWidth - clientWidth - scrollLeft : -scrollLeft
  }

  followsInCurrentTab(event, target) {
    return event.button === 0 &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.shiftKey &&
      (!target || target === "_self")
  }

  behavior() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
  }

  view() {
    return {
      left: this.previousTarget.getBoundingClientRect().right,
      right: this.nextTarget.getBoundingClientRect().left
    }
  }

  toggle(control, visible) {
    control.classList.toggle("invisible", !visible)
    control.classList.toggle("pointer-events-none", !visible)
  }

  get activeChip() {
    return this.chipTargets.find((chip) => chip.getAttribute("aria-current") === "page")
  }
}
